// client/src/components/MetricsCards.js
import React, { useState } from 'react';

const MetricsCards = ({ 
  portfolioStats, 
  performanceData, 
  preferredCurrency, 
  formatCurrencyValue, 
  formatNumber 
}) => {
  const [earningsPeriod, setEarningsPeriod] = useState('year');

  // Get earnings value based on selected period
  const getEarningsValue = () => {
    const summary = performanceData.summary;
    switch (earningsPeriod) {
      case 'day':
        const dailyGrowth = performanceData.growth.length > 0 ? 
          performanceData.growth[performanceData.growth.length - 1] : null;
        return dailyGrowth ? dailyGrowth.value : 0;
      case 'week':
        // Calculate last week's growth
        if (performanceData.growth.length >= 7) {
          const lastWeek = performanceData.growth.slice(-7);
          return lastWeek.reduce((sum, item) => sum + item.value, 0);
        }
        return 0;
      case 'year':
      default:
        return summary.totalGrowth || 0;
    }
  };

  // Get percentage based on selected period
  const getEarningsPercentage = () => {
    const summary = performanceData.summary;
    switch (earningsPeriod) {
      case 'day':
        return performanceData.growth.length > 0 ? 
          performanceData.growth[performanceData.growth.length - 1]?.percentageGrowth || 0 : 0;
      case 'week':
        return summary.periodGrowth || 0;
      case 'year':
      default:
        return summary.periodGrowth || 0;
    }
  };

  const earningsValue = getEarningsValue();
  const earningsPercentage = getEarningsPercentage();
  const isPositive = earningsValue >= 0;

  return (
    <>
      {/* Mobile: horizontal scroll pills */}
      <div className="metrics-pills-scroll">
        <div className="metrics-pills">
          <div className="metrics-pill pill-highlight">
            <span className="pill-label">Gesamtwert</span>
            <span className="pill-value">{formatCurrencyValue(portfolioStats.totalValue, preferredCurrency)}</span>
          </div>
          <div className={`metrics-pill ${isPositive ? 'pill-positive' : 'pill-negative'}`}>
            <span className="pill-label">Entwicklung</span>
            <span className={`pill-value ${isPositive ? 'positive' : 'negative'}`}>
              {isPositive ? '+' : ''}{formatCurrencyValue(earningsValue, preferredCurrency)}
            </span>
            <span className={`pill-sub ${isPositive ? 'positive' : 'negative'}`}>
              {Math.abs(earningsPercentage).toFixed(1)}%
            </span>
          </div>
          <div className="metrics-pill">
            <span className="pill-label">Kaufwert</span>
            <span className="pill-value">{formatCurrencyValue(portfolioStats.totalPurchasePrice, preferredCurrency)}</span>
          </div>
          <div className="metrics-pill">
            <span className="pill-label">Dividenden</span>
            <span className="pill-value">{formatCurrencyValue(portfolioStats.totalDividends, preferredCurrency)}</span>
            <span className="pill-sub">{formatNumber(portfolioStats.totalDividendYield)}%</span>
          </div>
          <div className="metrics-pill">
            <span className="pill-label">Aktien</span>
            <span className="pill-value">{portfolioStats.numberOfStocks}</span>
          </div>
          <div className="metrics-pill">
            <span className="pill-label">Ø KGV</span>
            <span className="pill-value">{formatNumber(portfolioStats.averagePERatio)}</span>
          </div>
        </div>
      </div>

      {/* Desktop: grid cards (unchanged) */}
      <div className="metrics-cards-container">
        <div className="metrics-card highlight">
          <div className="card-header">
            <span className="card-title">Wertentwicklung</span>
            <div className="period-toggle">
              <button
                className={`period-btn ${earningsPeriod === 'day' ? 'active' : ''}`}
                onClick={() => setEarningsPeriod('day')}
              >1T</button>
              <button
                className={`period-btn ${earningsPeriod === 'week' ? 'active' : ''}`}
                onClick={() => setEarningsPeriod('week')}
              >1W</button>
              <button
                className={`period-btn ${earningsPeriod === 'year' ? 'active' : ''}`}
                onClick={() => setEarningsPeriod('year')}
              >1J</button>
            </div>
          </div>
          <div className="card-value-container">
            <div className={`card-value ${isPositive ? 'positive' : 'negative'}`}>
              <i className={`fas fa-${isPositive ? 'arrow-up' : 'arrow-down'}`}></i>
              {formatCurrencyValue(Math.abs(earningsValue), preferredCurrency)}
            </div>
            <div className={`card-subvalue ${isPositive ? 'positive' : 'negative'}`}>
              {Math.abs(earningsPercentage).toFixed(2)}%
            </div>
          </div>
        </div>

        <div className="metrics-card">
          <div className="card-header">
            <span className="card-title">Gesamtwert</span>
            <i className="fas fa-wallet card-icon"></i>
          </div>
          <div className="card-value">
            {formatCurrencyValue(portfolioStats.totalValue, preferredCurrency)}
          </div>
        </div>

        <div className="metrics-card">
          <div className="card-header">
            <span className="card-title">Kaufwert</span>
            <i className="fas fa-tags card-icon"></i>
          </div>
          <div className="card-value">
            {formatCurrencyValue(portfolioStats.totalPurchasePrice, preferredCurrency)}
          </div>
        </div>

        <div className="metrics-card">
          <div className="card-header">
            <span className="card-title">Aktien</span>
            <i className="fas fa-layer-group card-icon"></i>
          </div>
          <div className="card-value">
            {portfolioStats.numberOfStocks}
          </div>
        </div>

        <div className="metrics-card">
          <div className="card-header">
            <span className="card-title">Dividenden (Jahr)</span>
            <i className="fas fa-hand-holding-usd card-icon"></i>
          </div>
          <div className="card-value-container">
            <div className="card-value">
              {formatCurrencyValue(portfolioStats.totalDividends, preferredCurrency)}
            </div>
            <div className="card-subvalue">
              {formatNumber(portfolioStats.totalDividendYield)}% Rendite
            </div>
          </div>
        </div>

        <div className="metrics-card">
          <div className="card-header">
            <span className="card-title">Durchschn. KGV</span>
            <i className="fas fa-balance-scale card-icon"></i>
          </div>
          <div className="card-value">
            {formatNumber(portfolioStats.averagePERatio)}
          </div>
        </div>
      </div>
    </>
  );
};

export default MetricsCards;